import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Section } from '@/shared/ui/layout/Section';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ChevronRightIcon } from '@/shared/ui/icons';
import { extractErrorMessage } from '@/shared/api/errors';
import { useMyParcelDebts } from '@/entities/parcels';
import { formatUsd } from '@/shared/utils/currency';

// Self-contained debts section. Owns its own data hook and renders nothing
// when the customer has no arrived-parcel debt, to keep the profile clean.
export const DebtsCard = () => {
  const { t } = useTranslation('profile');
  const navigate = useNavigate();
  const query = useMyParcelDebts();

  if (query.isLoading) {
    return (
      <Section title={t('debts.title')}>
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      </Section>
    );
  }

  if (query.isError) {
    return (
      <Section title={t('debts.title')}>
        <p className="text-sm text-red-600">
          {query.error ? extractErrorMessage(query.error) : t('debts.error')}
        </p>
      </Section>
    );
  }

  const data = query.data;
  const parcels = data?.parcels ?? [];
  const total = Number(data?.total_debt ?? 0);

  // Hide entirely when there is nothing owed.
  if (parcels.length === 0 && !(total > 0)) return null;

  return (
    <Section title={t('debts.title')}>
      <div className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
        <span className="text-sm font-medium text-amber-800">{t('debts.total')}</span>
        <span className="text-base font-semibold text-amber-800">{formatUsd(total)}</span>
      </div>

      <ul className="flex flex-col divide-y divide-slate-100">
        {parcels.map((parcel) => (
          <li key={parcel.id}>
            <button
              type="button"
              onClick={() => navigate(`/parcels/${parcel.id}`)}
              className="flex w-full items-center justify-between gap-3 py-3 text-left active:opacity-70"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-ink">{parcel.name}</span>
                <span className="block truncate text-xs text-ink-muted">{parcel.trek_id}</span>
              </span>
              <span className="shrink-0 text-sm font-semibold text-ink">
                {formatUsd(Number(parcel.debt_amount))}
              </span>
              <ChevronRightIcon className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
            </button>
          </li>
        ))}
      </ul>
    </Section>
  );
};
